import { Flex, Tag, Text } from '@spotify-internal/encore-web';
import {
  StringUtils,
  extractLastNameComponent,
  scaleWeights,
} from '@spotify/confidence-core-react';
import {
  GetWorkflowInstanceQuery,
  ReanalysisData,
  getTypeOrNull,
} from '@spotify/confidence-graphql';
import { useSetResolvedTitle } from '@spotify/plugin-confidence-projects-react';
import {
  moduleHelpers,
  parseWorkflowInstanceName,
  useGetWorkflowInstance,
} from '@spotify/plugin-confidence-workflows-react';

import {
  AudienceSummary,
  KeyValueRow,
  MetricRow,
  PreviewShell,
  SummarySection,
  TreatmentBarList,
  TypeStatusRow,
  formatAllocation,
  formatDateRange,
  resolveDisplayName,
} from './components';

type WorkflowInstance = Extract<
  NonNullable<GetWorkflowInstanceQuery['workflowInstance']>,
  { __typename: 'WorkflowV1WorkflowInstance' }
>;

type ModuleData = WorkflowInstance['moduleData2'];
type AbTestModule = NonNullable<ModuleData['abtest']>;
type MetricsModule = NonNullable<ModuleData['metrics']>;

const buildReanalysisTreatments = (data: ReanalysisData) =>
  (data.treatments ?? []).map((treatment, index) => {
    const label =
      treatment.displayName ||
      treatment.name ||
      treatment.variantKey ||
      `Treatment ${index + 1}`;
    return { label, weight: Number(treatment.weight) || 0 };
  });

const buildTreatments = (abtest: AbTestModule) => {
  const scaled = scaleWeights(
    (abtest.treatments ?? []).map(treatment => ({
      ...treatment,
      weight: Number(treatment.weight ?? 0),
    })),
  );
  return scaled.map((treatment, index) => {
    const variantName = getTypeOrNull(treatment.variant)?.name;
    const label =
      treatment.displayName ||
      treatment.name ||
      (variantName ? extractLastNameComponent(variantName) : undefined) ||
      `Treatment ${index + 1}`;
    return { label, weight: treatment.weight };
  });
};

const isSuccessRole = (
  metricRole: MetricsModule['metrics'][number]['metricRole'],
): boolean => 'successMetricKind' in metricRole;

interface WorkflowArtifactPreviewProps {
  resourceName: string;
  label: string;
}

export const WorkflowArtifactPreview = ({
  resourceName,
  label,
}: WorkflowArtifactPreviewProps) => {
  const { workflow, id } = parseWorkflowInstanceName(resourceName);
  const { workflowInstance, loading, errorMessage } = useGetWorkflowInstance({
    workflowId: workflow,
    instanceId: id,
  });

  const previewError =
    errorMessage ?? (!loading && !workflowInstance ? 'not-found' : undefined);

  useSetResolvedTitle(
    workflowInstance?.displayName || extractLastNameComponent(resourceName),
  );

  const moduleData = workflowInstance?.moduleData2;
  const abtest = moduleData?.abtest ?? null;
  const rollout = moduleData?.rollout ?? null;
  const flagsModule = moduleData?.flags ?? null;
  const metricsModule = moduleData?.metrics ?? null;
  const description =
    moduleData?.hypothesis?.text || rollout?.description || '';
  const planningData = moduleData?.planning ?? null;
  const reanalysisData =
    moduleHelpers.getModuleData<ReanalysisData>(
      workflowInstance?.moduleData,
      'reanalysis',
    ) ?? null;
  const entity = getTypeOrNull(metricsModule?.entity);
  const assignmentTable = getTypeOrNull(metricsModule?.assignmentTable);
  const surfaces = (workflowInstance?.surfaces ?? [])
    .map(surface => getTypeOrNull(surface))
    .filter((surface): surface is NonNullable<typeof surface> =>
      Boolean(surface),
    );
  const successMetrics = (metricsModule?.metrics ?? []).filter(m =>
    isSuccessRole(m.metricRole),
  );
  const guardrailMetrics = (metricsModule?.metrics ?? []).filter(
    m => !isSuccessRole(m.metricRole),
  );
  const allMetrics = [
    ...successMetrics.map(m => ({ metric: m, role: 'Success metric' })),
    ...guardrailMetrics.map(m => ({ metric: m, role: 'Guardrail metric' })),
  ];
  const hasMetricConfig = entity || assignmentTable || surfaces.length > 0;
  const abtestTreatments = abtest ? buildTreatments(abtest) : [];
  const reanalysisTreatments = reanalysisData
    ? buildReanalysisTreatments(reanalysisData)
    : [];
  const treatments =
    abtestTreatments.length > 0 ? abtestTreatments : reanalysisTreatments;

  const rolloutVariant = rollout ? getTypeOrNull(rollout.variant) : null;
  const rolloutFeatureName = rolloutVariant
    ? (extractLastNameComponent(rolloutVariant.name) ?? rolloutVariant.name)
    : null;
  const rolloutReach = formatAllocation(rollout?.reach);
  const planned = planningData?.planned;
  const actual = planningData?.actual;
  const plannedRange =
    planned?.startTime || planned?.endTime
      ? formatDateRange(planned.startTime, planned.endTime)
      : formatDateRange(actual?.startTime, actual?.endTime);
  const assignmentRange = reanalysisData
    ? formatDateRange(reanalysisData.startTime, reanalysisData.endTime)
    : undefined;

  return (
    <PreviewShell loading={loading} error={previewError}>
      {workflowInstance && (
        <>
          <TypeStatusRow typeLabel={label} state={workflowInstance.state} />
          {description && (
            <Text variant="bodySmall" semanticColor="textSubdued">
              {description}
            </Text>
          )}
          {flagsModule && (
            <SummarySection title="Audience">
              <Flex direction="column" gap="tighter-2">
                <AudienceSummary
                  targetingKeySelector={flagsModule.targetingKeySelector}
                  allocationProportion={
                    getTypeOrNull(flagsModule.segment)?.allocation?.proportion
                      ?.value
                  }
                  criteriaCount={
                    getTypeOrNull(flagsModule.segment)?.targeting?.criteria
                      ?.length ?? 0
                  }
                />
                {flagsModule.stickyAssignments && (
                  <KeyValueRow label="Sticky">
                    <Tag>Enabled</Tag>
                  </KeyValueRow>
                )}
              </Flex>
            </SummarySection>
          )}
          {rollout && (
            <SummarySection title="Feature">
              <Flex direction="column" gap="tighter-2">
                {rolloutFeatureName && (
                  <KeyValueRow label="Variant">
                    <Tag>{rolloutFeatureName}</Tag>
                  </KeyValueRow>
                )}
                {rolloutReach && (
                  <KeyValueRow label="Reach">
                    <Text variant="bodySmall">{rolloutReach}</Text>
                  </KeyValueRow>
                )}
              </Flex>
            </SummarySection>
          )}
          {(abtest || reanalysisData) && (
            <SummarySection title="Treatments">
              <TreatmentBarList treatments={treatments} />
            </SummarySection>
          )}
          {(plannedRange || assignmentRange) && (
            <SummarySection title="Planning">
              <Flex direction="column" gap="tighter-2">
                {plannedRange && (
                  <KeyValueRow label="Runtime">
                    <Text variant="bodySmall">{plannedRange}</Text>
                  </KeyValueRow>
                )}
                {assignmentRange && (
                  <KeyValueRow label="Assignment">
                    <Text variant="bodySmall">{assignmentRange}</Text>
                  </KeyValueRow>
                )}
              </Flex>
            </SummarySection>
          )}
          {allMetrics.length > 0 && (
            <SummarySection title="Metrics">
              <Flex direction="column" gap="tighter-2">
                {allMetrics.map(({ metric, role }, index) => {
                  const resolved = getTypeOrNull(metric.metric);
                  return (
                    <MetricRow
                      key={resolved?.name ?? `m-${index}`}
                      name={resolved?.name ?? ''}
                      displayName={resolved?.displayName}
                      role={role}
                    />
                  );
                })}
              </Flex>
            </SummarySection>
          )}
          {hasMetricConfig && (
            <SummarySection title="Metric config">
              <Flex direction="column" gap="tighter-2">
                {entity && (
                  <KeyValueRow label="Entity">
                    <Tag>{resolveDisplayName(entity)}</Tag>
                  </KeyValueRow>
                )}
                {assignmentTable && (
                  <KeyValueRow label="Assign table">
                    <Tag>{resolveDisplayName(assignmentTable)}</Tag>
                  </KeyValueRow>
                )}
                {surfaces.length > 0 && (
                  <KeyValueRow
                    label={StringUtils.pluralize(surfaces.length, 'Surface')}
                  >
                    {surfaces.map(surface => (
                      <Tag key={surface.name}>
                        {resolveDisplayName(surface)}
                      </Tag>
                    ))}
                  </KeyValueRow>
                )}
              </Flex>
            </SummarySection>
          )}
        </>
      )}
    </PreviewShell>
  );
};
